// ABOUTME: Hook driving the AI square-suggestion generate/review/add state machine.
// ABOUTME: Delegates all API calls to the onGenerate/onAddSquares callbacks it's given.
import { useState } from 'react'

export type AiSquareGenerationState = 'idle' | 'generating' | 'reviewing' | 'adding' | 'error'

interface UseAiSquareGenerationOptions {
  onGenerate: (topic: string) => Promise<string[]>
  onAddSquares: (titles: string[]) => Promise<void>
  defaultTopic?: string
}

export function useAiSquareGeneration({ onGenerate, onAddSquares, defaultTopic = '' }: UseAiSquareGenerationOptions) {
  const [topic, setTopic] = useState(defaultTopic)
  const [state, setState] = useState<AiSquareGenerationState>('idle')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [addError, setAddError] = useState<string | null>(null)

  function reset() {
    setTopic(defaultTopic)
    setState('idle')
    setSuggestions([])
    setAddError(null)
  }

  async function generate(overrideTopic?: string) {
    const effectiveTopic = overrideTopic ?? topic
    if (overrideTopic !== undefined) setTopic(overrideTopic)
    setState('generating')
    try {
      const results = await onGenerate(effectiveTopic)
      setSuggestions(results)
      setState('reviewing')
    } catch {
      setState('error')
    }
  }

  async function addToPack(): Promise<boolean> {
    setState('adding')
    try {
      await onAddSquares(suggestions)
      return true
    } catch {
      setState('reviewing')
      setAddError('Something went wrong adding squares. Please try again.')
      return false
    }
  }

  function removeSuggestion(index: number) {
    setSuggestions((prev) => prev.filter((_, i) => i !== index))
  }

  async function retry() {
    setSuggestions([])
    await generate()
  }

  return { topic, setTopic, state, suggestions, addError, generate, addToPack, removeSuggestion, retry, reset }
}
